import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { readJson, writeJson } from '../utils/storage';

const SiteContext = createContext();

const SITES_CACHE_KEY = 'sites_cache_v1';

export const useSite = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error('useSite must be used within a SiteProvider');
  }
  return context;
};

export const SiteProvider = ({ children }) => {
  const { user } = useAuth();
  const [currentSite, setCurrentSite] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);

  // Cargar sitios disponibles
  useEffect(() => {
    const loadSites = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          setLoading(false);
          return;
        }

        const { data } = await axios.get('/sites', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setSites(data);
        writeJson(SITES_CACHE_KEY, { sites: data, savedAt: Date.now() });

        // Establecer sitio actual según el rol del usuario
        if (user) {
          console.log('🔧 SiteContext - Setting up site for user:', { 
            role: user.role, 
            user_site_id: user.site_id,
            available_sites: data.length 
          });
          
          // Si el usuario tiene site_id, usarlo
          if (user.site_id) {
            const userSite = data.find(s => s.id === user.site_id);
            const selectedSite = userSite || data[0];
            console.log('✅ SiteContext - User has site_id, setting:', selectedSite);
            setCurrentSite(selectedSite);
          } else {
            // Si no tiene site_id (admin_master o admin_analytics), usar el guardado o el primero
            const savedSiteId = localStorage.getItem('selected_site_id');
            if (savedSiteId) {
              const savedSite = data.find(s => s.id === parseInt(savedSiteId));
              const selectedSite = savedSite || data[0];
              console.log('✅ SiteContext - Using saved site:', selectedSite);
              setCurrentSite(selectedSite);
            } else {
              console.log('✅ SiteContext - No saved site, using first:', data[0]);
              setCurrentSite(data[0]);
              // Guardar en localStorage para próxima vez
              if (data[0]) {
                localStorage.setItem('selected_site_id', data[0].id.toString());
              }
            }
          }
        }
      } catch (error) {
        console.error('Error loading sites:', error);

        // Offline fallback: use cached sites (if any) or a minimal site object from user.site_id
        const cached = readJson(SITES_CACHE_KEY, null);
        const cachedSites = cached?.sites;

        if (Array.isArray(cachedSites) && cachedSites.length > 0) {
          setSites(cachedSites);
          if (user?.site_id) {
            const userSite = cachedSites.find(s => s.id === user.site_id);
            setCurrentSite(userSite || cachedSites[0]);
          } else {
            const savedSiteId = localStorage.getItem('selected_site_id');
            if (savedSiteId) {
              const savedSite = cachedSites.find(s => s.id === parseInt(savedSiteId));
              setCurrentSite(savedSite || cachedSites[0]);
            } else {
              setCurrentSite(cachedSites[0]);
            }
          }
        } else if (user?.site_id) {
          const minimal = { id: user.site_id, code: '', name: 'Sitio', active: true };
          setSites([minimal]);
          setCurrentSite(minimal);
        }
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadSites();
    } else {
      setLoading(false);
    }
  }, [user]);

  // Cambiar sitio (solo permitido para admin_analytics)
  const changeSite = (siteId) => {
    if (!user) return;

    // Solo admin_analytics y admin_master pueden cambiar de sitio
    if (user.role !== 'admin_analytics' && user.role !== 'admin_master') {
      console.warn('User is not authorized to change site');
      return;
    }

    const site = sites.find(s => s.id === siteId);
    if (site) {
      setCurrentSite(site);
      localStorage.setItem('selected_site_id', siteId.toString());
    }
  };

  // Verificar si el usuario puede cambiar de sitio
  const canChangeSite = () => {
    return user && (user.role === 'admin_analytics' || user.role === 'admin_master');
  };

  // Obtener el site_id actual para las queries
  const getCurrentSiteId = () => {
    return currentSite?.id || user?.site_id || null;
  };

  // Obtener el código del sitio actual (RB, BRO, KTY)
  const getCurrentSiteCode = () => {
    return currentSite?.code || '';
  };

  const value = {
    currentSite,
    sites,
    loading,
    changeSite,
    canChangeSite,
    getCurrentSiteId,
    getCurrentSiteCode
  };

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};
